import {
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  Button,
  Box,
  TextField,
  Stack,
  Autocomplete,
  IconButton,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import {MaterialReactTable} from "material-react-table";
import {useEffect, useState} from "react";
import {LedgerType, TransactionHistory} from "./types";
import {historyColumns} from "./HistoryColumns";
import {CloseIcon} from "../../../minimals/theme/overrides/CustomIcons";
import {useParams} from "react-router-dom";

const TransactionHistoryModal = ({
                                   open,
                                   closeModal,
                                   transaction,
                                   onChangeActionCompleted,
                                   ledgerName
                                 }: {
  open: boolean;
  closeModal: () => void;
  transaction: LedgerType;
  ledgerName: string;
  onChangeActionCompleted: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const {orderNumber} = useParams();
  const [actionLoading, setActionLoading] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState<TransactionHistory[]>([]);
  const [moveLoading, setMoveLoading] = useState(false);
  const isWireTransaction =
      transaction.type === "Wire" || transaction.typeDescription?.toLowerCase().includes("wire");


  const getTransactionHistory = async () => {
    setLoading(true);
    try {
      const {data} = await axios.get(
          `/proxy/api/ledgers/GetTransactionHistory?transactionId=${transaction.id}&orderNumber=${orderNumber}`
      );
      setTransactionHistory(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTransactionHistory();
  }, []);

  const handleVoid = async () => {
    await makeChangeRequest(isWireTransaction ? "VoidPostedWire" : "VoidPostedCheck");
  };

  const handleRepost = async () => {
    await makeChangeRequest("RepostVoidedCheck");
  };

  const makeChangeRequest = async (endpoint: string) => {
    setActionLoading(true);
    await axios.post(`/proxy/api/ledgers/${endpoint}`, {
      orderNumber,
      transactionId: transaction.id,
      reason
    });
    setActionLoading(false);
    onChangeActionCompleted();
  };

  const handleMoveToEarnestMoney = async () => {
    setMoveLoading(true);
    try {
      await axios.post('/proxy/api/ledgers/MoveTransactionAmountToEarnestMoney', {
        transactionId: transaction.id,
        ledgerName
      });
      await getTransactionHistory();
      onChangeActionCompleted?.();
    } finally {
      setMoveLoading(false);
    }
  };


  const getBottomSection = () => {
    const isCheck = transaction.type === "Check";
    const isWire = isWireTransaction;

    if (!isCheck && !isWire) return;

    const isPosted = transaction.status === "Posted";
    const isVoided = transaction.status === "Voided";

    if (isWire && !isPosted) return;
    if (isCheck && !isPosted && !isVoided) return;

    const actionLabel = isPosted ? "Void" : "Repost";
    const sectionTitle = `${actionLabel} ${isWire ? "Wire" : "Check"}`;
    const buttonColor = isPosted ? "error" : "primary";

    return (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            {sectionTitle}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
                label="Reason"
                variant="outlined"
                fullWidth
                value={reason}
                onChange={(e) => setReason(e.target.value)}
            />
            <Button
                variant="contained"
                color={buttonColor as "primary" | "error"}
                disabled={!reason.trim()}
                onClick={isPosted ? handleVoid : handleRepost}
            >
              {actionLoading ? <CircularProgress size={24} color="inherit"/> : actionLabel}
            </Button>
          </Stack>
        </Box>
    );
  };

  return (
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant="h4" textAlign="center">
            Transaction History
          </Typography>
        </DialogTitle>
        <DialogContent sx={{paddingBottom: 3, position: "relative"}}>
          <IconButton
              onClick={closeModal}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                zIndex: 2, // stays on top of overlay
              }}
          >
            <CloseIcon/>
          </IconButton>

          {/* MAIN CONTENT */}
          <Box paddingTop={1} position="relative">
            {loading ? (
                <Box
                    sx={{
                      height: 200, // ✅ fixed height while loading, prevents huge modal
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                >
                  <CircularProgress/>
                </Box>
            ) : (
                <MaterialReactTable
                    enableStickyHeader
                    columns={historyColumns}
                    data={transactionHistory}
                    initialState={{
                      density: "compact",
                      pagination: {pageIndex: 0, pageSize: 30}
                    }}
                    muiTableBodyProps={{
                      sx: {
                        "& .MuiTableRow-root:hover .MuiTableCell-root": {
                          backgroundColor: "inherit !important"
                        }
                      }
                    }}
                    state={{showSkeletons: loading}}
                    enableSorting={false}
                    enableGlobalFilter={false}
                    enableFilters={false}
                    enablePagination={false}
                    enableColumnResizing={false}
                    enableColumnActions={false}
                    enableFullScreenToggle={false}
                    enableDensityToggle={false}
                    enableHiding={false}
                />
            )}
            {!transaction.isDebit &&
                <Box mt={3} display="flex" alignItems="center" gap={1} sx={{opacity: 0.8}}>
                  <Checkbox
                      checked={!!transaction.isAppliedToEarnestMoney}
                      disabled
                  />
                  <Typography fontWeight={500}>
                    Applied To Earnest Money
                  </Typography>
                  <Button
                      variant="contained"
                      size="small"
                      onClick={handleMoveToEarnestMoney}
                      disabled={transaction.isAppliedToEarnestMoney || moveLoading}
                  >
                    {moveLoading
                        ? <CircularProgress size={20} color="inherit" />
                        : "Move to Earnest Money"
                    }
                  </Button>

                </Box>
                
            }
          </Box>


          {getBottomSection()}
        </DialogContent>
      </Dialog>
  );
};

export default TransactionHistoryModal;
